import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Mail, Phone, MapPin, Clock, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { BOOKING_URL, THERAPISTS, BUSINESS, INSURANCE_PROVIDERS } from "@/lib/constants";
import { absoluteUrl } from "@/lib/seo-config";
import { submitContact } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useFormTracking } from "@/hooks/useFormTracking";

const contactBreadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: absoluteUrl("/") },
    { "@type": "ListItem", position: 2, name: "Contact", item: absoluteUrl("/contact") },
  ],
};

export default function Contact() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    hp: "",  // honeypot — real users don't fill this
  });
  // Dwell-time capture: bots submit instantly, humans take a moment.
  const [mountedAt] = useState(() => Date.now());
  const formTrack = useFormTracking("contact");

  // Pre-fill subject + message when arriving from /corporate-wellness or
  // /learn (e.g. ?subject=Corporate%20Wellness%20Inquiry).
  useEffect(() => {
    const subj = searchParams.get("subject") || "";
    const msg = searchParams.get("message") || "";
    if (subj || msg) {
      setForm((prev) => ({
        ...prev,
        subject: subj || prev.subject,
        message: msg || prev.message,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const change = (k) => (e) => {
    const v = e.target.value;
    setForm({ ...form, [k]: v });
    formTrack.onChange(k, v);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and a message.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        message: form.message.trim(),
        hp: form.hp || "",
        elapsed_seconds: Math.max(0, Math.floor((Date.now() - mountedAt) / 1000)),
      };
      if (form.phone.trim()) payload.phone = form.phone.trim();
      if (form.subject.trim()) payload.subject = form.subject.trim();
      await submitContact(payload);
      track("contact_submit", { has_phone: Boolean(payload.phone), has_subject: Boolean(payload.subject) });
      formTrack.markSubmitted();
      toast.success("Thank you — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "", hp: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-white">
      <SEO
        title="Contact — Ashlan Clinic, Hamilton, Bermuda"
        description="Visit us at 48 Cedar Avenue, Hamilton HM 11. Call (441) 292-6830 or email info@ashlanclinic.com. Insurance accepted: CG Insurance, Allshores."
        path="/contact"
        schemas={[contactBreadcrumb]}
      />
      <section className="border-b border-[#e5e7eb] bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
          <span className="ash-eyebrow">Contact</span>
          <h1 className="mt-6 text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.02] tracking-tight text-[#080808] max-w-4xl">
            Get in touch with the clinic.
          </h1>
          <p className="mt-8 text-lg md:text-xl text-[#3C4048] max-w-2xl leading-relaxed">
            Speak with our team about treatment, insurance coverage, corporate
            programmes or product enquiries.
          </p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left - info */}
          <div className="lg:col-span-5">
            <span className="ash-eyebrow">Clinic Details</span>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-[#080808] leading-tight">
              We’re here to help you start feeling better.
            </h2>

            <ul className="mt-10 flex flex-col gap-6">
              <li className="flex items-start gap-4" data-testid="contact-address">
                <MapPin size={22} strokeWidth={1.5} className="text-[#3A7A8C] mt-1" />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] font-semibold text-[#6B7280]">
                    Visit
                  </div>
                  <p className="mt-1 text-[#080808] font-medium leading-relaxed">
                    {BUSINESS.address1}
                    <br />
                    {BUSINESS.address2}
                    <br />
                    {BUSINESS.country}
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-phone">
                <Phone size={22} strokeWidth={1.5} className="text-[#3A7A8C] mt-1" />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] font-semibold text-[#6B7280]">
                    Call
                  </div>
                  <a
                    href={BUSINESS.phoneHref}
                    className="mt-1 inline-block text-[#080808] font-medium hover:text-[#3A7A8C]"
                  >
                    {BUSINESS.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-email">
                <Mail size={22} strokeWidth={1.5} className="text-[#3A7A8C] mt-1" />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] font-semibold text-[#6B7280]">
                    Email
                  </div>
                  <a
                    href={`mailto:${BUSINESS.email}`}
                    className="mt-1 inline-block text-[#080808] font-medium hover:text-[#3A7A8C]"
                  >
                    {BUSINESS.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-hours">
                <Clock size={22} strokeWidth={1.5} className="text-[#3A7A8C] mt-1" />
                <div>
                  <div className="text-xs uppercase tracking-[0.22em] font-semibold text-[#6B7280]">
                    Hours
                  </div>
                  <p className="mt-1 text-[#080808] font-medium leading-relaxed">
                    Monday – Friday · 8am – 7pm
                    <br />
                    Saturday · 9am – 3pm
                  </p>
                </div>
              </li>
            </ul>

            <div className="mt-10 flex items-center gap-4">
              <a
                href={BOOKING_URL}
                data-testid="contact-book-button"
                className="ash-btn-secondary"
              >
                Book Online <ArrowUpRight size={16} />
              </a>
              <span
                data-testid="contact-tagline"
                className="text-[#3A7A8C] font-bold tracking-[0.12em] text-sm uppercase"
              >
                {BUSINESS.tagline}
              </span>
            </div>

            {/* Insurance Providers */}
            <div className="mt-12 pt-10 border-t border-[#e5e7eb]">
              <div className="ash-eyebrow flex items-center gap-2">
                <ShieldCheck size={14} strokeWidth={1.8} className="text-[#3A7A8C]" />
                Insurance Providers
              </div>
              <ul className="mt-4 flex flex-col gap-3">
                {INSURANCE_PROVIDERS.map((p) => (
                  <li key={p.name}>
                    <a
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`insurance-link-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="ash-link-underline"
                    >
                      {p.name} <ArrowUpRight size={14} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right - form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#F5F5F5] p-8 md:p-12"
            >
              <span className="ash-eyebrow">Send Us a Message</span>
              <h3 className="mt-3 text-2xl md:text-3xl font-bold text-[#080808] tracking-tight">
                Tell us what brings you in.
              </h3>

              <div className="mt-8 grid sm:grid-cols-2 gap-6">
                {/* Honeypot — hidden from real users, bots will fill it. */}
                <div
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-10000px",
                    width: 1,
                    height: 1,
                    overflow: "hidden",
                  }}
                >
                  <label htmlFor="c-website">Website (leave blank)</label>
                  <input
                    id="c-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.hp}
                    onChange={(e) => setForm({ ...form, hp: e.target.value })}
                  />
                </div>
                <div>
                  <label className="ash-label" htmlFor="c-name">
                    Name
                  </label>
                  <input
                    id="c-name"
                    data-testid="contact-name-input"
                    className="ash-input"
                    placeholder="Full name"
                    value={form.name}
                    onChange={change("name")}
                    onFocus={() => formTrack.onFocus("name")}
                  />
                </div>
                <div>
                  <label className="ash-label" htmlFor="c-email">
                    Email
                  </label>
                  <input
                    id="c-email"
                    data-testid="contact-email-input"
                    type="email"
                    className="ash-input"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={change("email")}
                    onFocus={() => formTrack.onFocus("email")}
                  />
                </div>
                <div>
                  <label className="ash-label" htmlFor="c-phone">
                    Phone (optional)
                  </label>
                  <input
                    id="c-phone"
                    data-testid="contact-phone-input"
                    className="ash-input"
                    placeholder="+1 441…"
                    value={form.phone}
                    onChange={change("phone")}
                    onFocus={() => formTrack.onFocus("phone")}
                  />
                </div>
                <div>
                  <label className="ash-label" htmlFor="c-subject">
                    Subject (optional)
                  </label>
                  <input
                    id="c-subject"
                    data-testid="contact-subject-input"
                    className="ash-input"
                    placeholder="Treatment enquiry"
                    value={form.subject}
                    onChange={change("subject")}
                    onFocus={() => formTrack.onFocus("subject")}
                  />
                </div>
              </div>

              <div className="mt-6">
                <label className="ash-label" htmlFor="c-message">
                  Message
                </label>
                <textarea
                  id="c-message"
                  data-testid="contact-message-input"
                  className="ash-input ash-textarea"
                  placeholder="Tell us a little about what you'd like help with…"
                  value={form.message}
                  onChange={change("message")}
                  onFocus={() => formTrack.onFocus("message")}
                />
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="ash-btn-primary disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send Message"}
                  <ArrowRight size={16} />
                </button>
                <span className="text-sm text-[#6B7280]">
                  We typically respond within one business day.
                </span>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Therapist booking cards */}
      <section className="bg-[#F5F5F5] border-y border-[#e5e7eb] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
            <div className="md:col-span-8">
              <span className="ash-eyebrow">Book Direct</span>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-[#080808] leading-tight">
                Or book directly with your therapist.
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {THERAPISTS.map((t, i) => (
              <div
                key={t.slug}
                data-testid={`contact-therapist-card-${i}`}
                className="bg-white border border-[#e5e7eb] p-8 flex flex-col"
              >
                <div className="aspect-[3/2] sm:aspect-[4/5] overflow-hidden bg-[#F5F5F5] -mx-8 -mt-8 mb-6 flex items-center justify-center">
                  <img
                    src={t.photo}
                    alt={t.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <h3 className="text-2xl font-bold text-[#080808] leading-tight">
                  {t.name}
                </h3>
                <p className="mt-2 text-sm font-semibold text-[#3A7A8C] uppercase tracking-[0.14em]">
                  {t.credentials} · {t.role}
                </p>
                <a
                  href={t.bookingUrl}
                  data-testid={`contact-book-therapist-${i}`}
                  className="ash-btn-primary mt-6 self-start"
                >
                  Book with {t.name.split(" ")[0]}
                  <ArrowUpRight size={16} />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
